import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/queryClient'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/toaster'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { AuthProvider } from './contexts/AuthContext'
import { UserProvider } from './contexts/UserContext'
import { UseCaseProvider } from './contexts/UseCaseContext'
import { TeamProvider } from './contexts/TeamContext'
import { VendorProvider } from './contexts/VendorContext'
import { DashboardEditProvider } from './contexts/DashboardEditContext'
import { AssessmentProvider } from './contexts/AssessmentContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { AuthRequired, AuthOnlyRequired, TenantRequired, AdminRequired, AppRequired, FeatureRequired, PermissionRequired, AnyPermissionRequired } from './components/ProtectedRoute'
// Top-level force-password-change redirect mounted outside the lazy
// ``<Suspense>`` boundary. See component header for the React 18
// lazy-Suspense race rationale.
import ForcePasswordChangeGate from './components/ForcePasswordChangeGate'
import { useAuth } from './contexts/AuthContext'
import { AppLayout } from './components/layout/AppLayout'
import { Loader2 } from 'lucide-react'
import { PERMISSIONS } from './config/permissions'

// Page-level lazy imports
// Auth pages live under ./pages/auth/. Legacy aliases at
// ./pages/Login.jsx / NewLogin.jsx / MFAChallenge.jsx etc. are deleted.
const LoginPage = lazy(() => import('./pages/auth/Login'))
const RegisterPage = lazy(() => import('./pages/Register'))
const TenantSelectorPage = lazy(() => import('./pages/TenantHub'))
const ChangePasswordPage = lazy(() => import('./pages/auth/ForcePasswordChange'))
const AccountSettings = lazy(() => import('./pages/AccountSettings'))
const OnboardingPage = lazy(() => import('./pages/auth/Onboarding'))
const PasswordResetRequestPage = lazy(() => import('./pages/auth/PasswordResetRequest'))
const PasswordResetConfirmPage = lazy(() => import('./pages/auth/PasswordResetConfirm'))
const Index = lazy(() => import('./pages/Index'))
const Readiness = lazy(() => import('./pages/Readiness'))
const ReviewResponses = lazy(() => import('./pages/ReviewResponses'))
const Assessment = lazy(() => import('./pages/Assessment'))
const Governance = lazy(() => import('./pages/Governance'))
const Initiatives = lazy(() => import('./pages/Initiatives'))
const InitiativeCreate = lazy(() => import('./pages/InitiativeCreate'))
const UseCaseDetail = lazy(() => import('./pages/UseCaseDetail'))
const Ideas = lazy(() => import('./pages/Ideas'))
const IdeaDetail = lazy(() => import('./pages/IdeaDetail'))
const AIWorkspace = lazy(() => import('./pages/AIWorkspace'))
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'))
const AiLeads = lazy(() => import('./pages/AiLeads'))
const Vendors = lazy(() => import('./pages/Vendors'))
const Marketplace = lazy(() => import('./pages/Marketplace'))
const Team = lazy(() => import('./pages/Team'))
const Insights = lazy(() => import('./pages/Insights'))
const CostManagement = lazy(() => import('./pages/CostManagementV2'))
const CostManagementActions = lazy(() => import('./pages/CostManagementActions'))
const SettingsPage = lazy(() => import('./pages/Settings'))
const UserSettings = lazy(() => import('./pages/UserSettings'))
const CurrentPlan = lazy(() => import('./pages/Settings/CurrentPlan'))
const MFAChallenge = lazy(() => import('./pages/auth/MFAChallenge'))
const MFASetup = lazy(() => import('./pages/auth/MFAEnrollment'))
const Guide = lazy(() => import('./pages/Guide'))
const Support = lazy(() => import('./pages/Support'))
const TicketDetail = lazy(() => import('./pages/TicketDetail'))
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'))
const PublicIdeaIntake = lazy(() => import('./pages/PublicIdeaIntake'))
const AdminLanding = lazy(() => import('./pages/AdminLanding'))
const AdminMembers = lazy(() => import('./pages/AdminMembers'))
const AdminInvitations = lazy(() => import('./pages/AdminInvitations'))
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation'))
const AdminSettings = lazy(() => import('./pages/AdminSettings'))
const AdminCurrency = lazy(() => import('./pages/AdminCurrency'))
const AdminSecurity = lazy(() => import('./pages/AdminSecurity'))
const AdminRoles = lazy(() => import('./pages/AdminRoles'))
const AdminRoleEdit = lazy(() => import('./pages/AdminRoleEdit'))
const AdminOrgUnits = lazy(() => import('./pages/admin/AdminOrgUnits'))
const OrgUnitSubtree = lazy(() => import('./pages/admin/OrgUnitSubtree'))
const Audit = lazy(() => import('./pages/Audit'))
const AdminIntegrations = lazy(() => import('./pages/AdminIntegrations'))
const AdminIdentityProviders = lazy(() => import('./pages/AdminIdentityProviders'))
const EntraDiscoveryReview = lazy(() => import('./pages/admin/EntraDiscoveryReview'))
const AdminPolicies = lazy(() => import('./pages/AdminPolicies'))
const AdminPolicyEdit = lazy(() => import('./pages/AdminPolicyEdit'))
// Self-service access requests.
const RequestAccess = lazy(() => import('./pages/RequestAccess'))
const MyAccessRequests = lazy(() => import('./pages/MyAccessRequests'))
const AccessRequestsQueue = lazy(() => import('./pages/admin/AccessRequestsQueue'))
const PolicyVersionTimeline = lazy(() => import('./pages/admin/PolicyVersionTimeline'))


function PageLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
        <span>Loading page</span>
      </div>
    </div>
  )
}

function PageSectionLoading() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center px-6 py-10">
      <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm text-muted-foreground shadow-sm">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
        <span>Loading page</span>
      </div>
    </div>
  )
}

function AppPageLoading() {
  return (
    <AppLayout>
      <PageSectionLoading />
    </AppLayout>
  )
}

function lazyPage(element, fallback = <PageLoading />) {
  return <Suspense fallback={fallback}>{element}</Suspense>
}

function appPage(element) {
  return <AppRequired>{lazyPage(element, <AppPageLoading />)}</AppRequired>
}

function permissionPage(permission, element) {
  return (
    <PermissionRequired permission={permission}>
      {lazyPage(element, <AppPageLoading />)}
    </PermissionRequired>
  )
}

function anyPermissionPage(permissions, element) {
  return (
    <AnyPermissionRequired permissions={permissions}>
      {lazyPage(element, <AppPageLoading />)}
    </AnyPermissionRequired>
  )
}

function adminPage(element) {
  return <AdminRequired>{lazyPage(element, <AppPageLoading />)}</AdminRequired>
}

function authPage(element) {
  return <AuthRequired>{lazyPage(element)}</AuthRequired>
}

function authOnlyPage(element) {
  return <AuthOnlyRequired>{lazyPage(element)}</AuthOnlyRequired>
}

function tenantPage(element) {
  return <TenantRequired>{lazyPage(element)}</TenantRequired>
}

function HomeRedirect() {
  const { isAuthenticated, hasTenant, onboardingCompleted, forcePasswordChange, mfaPending, mfaSetupNeeded, loading } = useAuth()
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }
  if (mfaPending) {
    if (mfaSetupNeeded) return <Navigate to="/mfa-setup" replace />
    return <Navigate to="/mfa-challenge" replace />
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (forcePasswordChange) return <Navigate to="/change-password" replace />
  if (!hasTenant) return <Navigate to="/select-tenant" replace />
  if (!onboardingCompleted) return <Navigate to="/onboarding" replace />
  return <Navigate to="/home" replace />
}

function SelectTenantRoute() {
  // DESIGN §6A — a single-tenant-mode user never uses the hub. The bound
  // JWT carries a tenant_id (so hasTenant is true and HomeRedirect never
  // sends them here), but a *direct* hit on /select-tenant (or any
  // switch-tenant entry) must still bounce back into the app rather than
  // show a hub they can't use. Non-single-tenant users see the hub as
  // before.
  const { singleTenantMode, hasTenant } = useAuth()
  if (singleTenantMode && hasTenant) {
    return <Navigate to="/home" replace />
  }
  return authPage(<TenantSelectorPage />)
}

function LegacyCostManagementRedirect() {
  const location = useLocation()
  const tail = location.pathname
    .replace(/^\/home\/cost-management\/?/, "")
    .replace(/^\/+/, "")
  const target = tail ? `/cost-management/${tail}` : "/cost-management/overview"
  return <Navigate to={`${target}${location.search}`} replace />
}

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <UserProvider>
            <TeamProvider>
              <VendorProvider>
                <UseCaseProvider>
                  <DashboardEditProvider>
                    <NotificationProvider>
                      <AssessmentProvider>
                        <Toaster />
                        <Sonner />
                        <BrowserRouter>
                          <ForcePasswordChangeGate />
                          <Suspense fallback={<PageLoading />}>
                            <Routes>
                              {/* Public routes */}
                              <Route path="/login" element={<LoginPage />} />
                              <Route path="/register" element={<RegisterPage />} />
                              <Route path="/verify-email" element={<VerifyEmail />} />
                              {/* Password reset (request + confirm). Public. */}
                              <Route path="/forgot-password" element={<PasswordResetRequestPage />} />
                              <Route path="/reset-password" element={<PasswordResetConfirmPage />} />
                              {/* Stub paths kept for backward compat — also point to the real password reset pages. */}
                              <Route path="/password-reset/request" element={<PasswordResetRequestPage />} />
                              <Route path="/password-reset/confirm" element={<PasswordResetConfirmPage />} />
                              <Route path="/ideas/intake/confirm" element={<PublicIdeaIntake />} />
                              {/* Public invitation accept page */}
                              <Route path="/invitations/accept" element={<AcceptInvitation />} />

                              {/* Change password — requires auth only (no forcePasswordChange redirect to avoid loop) */}
                              <Route
                                path="/change-password"
                                element={
                                  authOnlyPage(<ChangePasswordPage />)
                                }
                              />

                              {/* Account settings — requires auth only (no tenant) */}
                              <Route
                                path="/account-settings"
                                element={
                                  authOnlyPage(<AccountSettings />)
                                }
                              />

                              {/* Auth required: tenant selection.
                                  Single-tenant-mode users are redirected
                                  into the app (DESIGN §6A). */}
                              <Route
                                path="/select-tenant"
                                element={<SelectTenantRoute />}
                              />

                              {/* Onboarding — requires tenant but not onboarding_completed */}
                              <Route path="/onboarding" element={tenantPage(<OnboardingPage />)} />

                              {/* Main app — requires tenant AND onboarding completed */}
                              <Route path="/home" element={appPage(<Index />)} />
                              <Route path="/dashboard" element={<Navigate to="/home" replace />} />
                              <Route path="/readiness" element={appPage(<Readiness />)} />
                              <Route path="/readiness/responses/:dimension" element={appPage(<ReviewResponses />)} />
                              <Route path="/assessment" element={appPage(<Assessment />)} />
                              <Route path="/governance" element={<FeatureRequired feature="governance">{appPage(<Governance />)}</FeatureRequired>} />
                              <Route path="/initiatives" element={permissionPage(PERMISSIONS.INITIATIVES_READ, <Initiatives />)} />
                              <Route path="/initiatives/new" element={permissionPage(PERMISSIONS.INITIATIVES_WRITE, <InitiativeCreate />)} />
                              <Route path="/use-cases/:id" element={permissionPage(PERMISSIONS.INITIATIVES_READ, <UseCaseDetail />)} />
                              <Route path="/ideas" element={permissionPage(PERMISSIONS.IDEAS_READ, <Ideas />)} />
                              <Route path="/ideas/:id" element={permissionPage(PERMISSIONS.IDEAS_READ, <IdeaDetail />)} />
                              <Route path="/ai-workspace" element={appPage(<AIWorkspace />)} />
                              <Route path="/knowledge-base" element={permissionPage(PERMISSIONS.KNOWLEDGE_READ, <KnowledgeBase />)} />
                              <Route path="/ai-leads" element={<FeatureRequired feature="ai_leads">{permissionPage(PERMISSIONS.AI_LEADS_READ, <AiLeads />)}</FeatureRequired>} />
                              <Route path="/vendors" element={<FeatureRequired feature="vendors">{permissionPage(PERMISSIONS.VENDORS_READ, <Vendors />)}</FeatureRequired>} />
                              <Route path="/marketplace" element={appPage(<Marketplace />)} />
                              <Route path="/team" element={permissionPage(PERMISSIONS.MEMBERS_READ, <Team />)} />
                              <Route path="/insights" element={appPage(<Insights />)} />
                              {/* Cost Management — one page handles the four
                                  URL-driven tabs (overview / registry /
                                  usage-analytics / budgets). */}
                              <Route path="/cost-management/actions/*" element={<FeatureRequired feature="cost_management">{appPage(<CostManagementActions />)}</FeatureRequired>} />
                              <Route path="/cost-management" element={<FeatureRequired feature="cost_management">{permissionPage(PERMISSIONS.COST_MANAGEMENT_READ, <CostManagement />)}</FeatureRequired>} />
                              <Route path="/cost-management/:tab/*" element={<FeatureRequired feature="cost_management">{permissionPage(PERMISSIONS.COST_MANAGEMENT_READ, <CostManagement />)}</FeatureRequired>} />
                              <Route path="/home/cost-management" element={<LegacyCostManagementRedirect />} />
                              <Route path="/home/cost-management/*" element={<LegacyCostManagementRedirect />} />
                              <Route path="/settings" element={appPage(<SettingsPage />)} />
                              <Route path="/settings/plan" element={appPage(<CurrentPlan />)} />
                              <Route path="/user-settings" element={appPage(<UserSettings />)} />
                              <Route path="/mfa-challenge" element={lazyPage(<MFAChallenge />)} />
                              <Route path="/mfa-setup" element={lazyPage(<MFASetup />)} />
                              <Route path="/guide" element={appPage(<Guide />)} />
                              <Route path="/support" element={appPage(<Support />)} />
                              <Route path="/support/tickets/:id" element={appPage(<TicketDetail />)} />

                              {/* Admin required: admin pages (inside AppLayout via each component) */}
                              {/* Card-based admin landing (replaces the legacy redirect to /admin/members) */}
                              <Route path="/admin" element={<AdminRequired><AdminLanding /></AdminRequired>} />
                              <Route path="/admin/members" element={permissionPage(PERMISSIONS.MEMBERS_READ, <AdminMembers />)} />
                              {/* Retire legacy admin pages: redirect to the new landing */}
                              <Route path="/admin/pending" element={<Navigate to="/admin/members" replace />} />
                              <Route path="/admin/invites" element={<Navigate to="/admin/members" replace />} />
                              <Route path="/admin/panel" element={<Navigate to="/admin" replace />} />
                              <Route path="/admin/documents" element={<Navigate to="/admin" replace />} />
                              {/* Full admin invitation flow page */}
                              <Route path="/admin/invitations" element={anyPermissionPage([PERMISSIONS.INVITES_READ, PERMISSIONS.INVITES_CREATE, PERMISSIONS.MEMBERS_WRITE], <AdminInvitations />)} />
                              <Route path="/admin/security" element={permissionPage(PERMISSIONS.SETTINGS_READ, <AdminSecurity />)} />
                              <Route path="/admin/settings" element={permissionPage(PERMISSIONS.SETTINGS_READ, <AdminSettings />)} />
                              <Route path="/admin/currency" element={permissionPage(PERMISSIONS.COST_MANAGEMENT_READ, <AdminCurrency />)} />
                              <Route path="/admin/roles" element={permissionPage(PERMISSIONS.ROLES_READ, <AdminRoles />)} />
                              <Route path="/admin/roles/new" element={permissionPage(PERMISSIONS.ROLES_CREATE, <AdminRoleEdit />)} />
                              <Route path="/admin/roles/:id/edit" element={permissionPage(PERMISSIONS.ROLES_READ, <AdminRoleEdit />)} />
                              <Route path="/admin/org-units" element={permissionPage(PERMISSIONS.ORG_UNITS_READ, <AdminOrgUnits />)} />
                              {/* Org-Unit-Head-scoped subtree management.
                                  Gated on org_units:read (the backend gate on
                                  /subtree and /members reads). The page renders a
                                  graceful "no managed org units" state when the
                                  actor has the read permission but no direct
                                  memberships and no tenant-wide members:manage. */}
                              <Route path="/admin/my-org-unit" element={<PermissionRequired permission="org_units:read"><OrgUnitSubtree /></PermissionRequired>} />
                              {/* Audit query UI — gated by audit_log_entry:read */}
                              <Route
                                path="/audit"
                                element={
                                  <PermissionRequired permission="audit_log_entry:read">
                                    <Audit />
                                  </PermissionRequired>
                                }
                              />
                              {/* Legacy admin audit page redirects to the new query UI */}
                              <Route path="/admin/audit" element={<Navigate to="/audit" replace />} />
                              <Route path="/admin/integrations" element={permissionPage(PERMISSIONS.INTEGRATIONS_MANAGE, <AdminIntegrations />)} />
                              {/* idp:configure on the route guard matches the
                                  component-level gate (and the backend FGA
                                  requires(configure, tenant)); wildcard admins
                                  still pass via bucketGrants. */}
                              <Route path="/admin/identity-providers" element={permissionPage(PERMISSIONS.IDP_CONFIGURE, <AdminIdentityProviders />)} />
                              <Route path="/admin/identity-providers/microsoft-entra/discovery" element={permissionPage(PERMISSIONS.IDP_CONFIGURE, <EntraDiscoveryReview />)} />
                              {/* Policy authoring — list + edit pages.
                                  AdminRequired includes policy:read / policy:configure
                                  in its permission OR list, so a policy admin can land
                                  on /admin/* without member/role/settings perms.

                                  Additionally wrapped in FeatureRequired feature="abac";
                                  mirrors the backend require_feature("abac") on the
                                  policy authoring routers. When the tenant has ABAC
                                  disabled the routes redirect to /home rather than
                                  render a page whose API calls would 403. */}
                              <Route path="/admin/policies" element={<FeatureRequired feature="abac">{anyPermissionPage([PERMISSIONS.POLICY_READ, PERMISSIONS.POLICY_CONFIGURE], <AdminPolicies />)}</FeatureRequired>} />
                              <Route path="/admin/policies/new" element={<FeatureRequired feature="abac">{permissionPage(PERMISSIONS.POLICY_CONFIGURE, <AdminPolicyEdit />)}</FeatureRequired>} />
                              <Route path="/admin/policies/:id/edit" element={<FeatureRequired feature="abac">{anyPermissionPage([PERMISSIONS.POLICY_READ, PERMISSIONS.POLICY_CONFIGURE], <AdminPolicyEdit />)}</FeatureRequired>} />
                              {/* Self-service access request flow.
                                  /request-access + /my-requests are tenant-member gated;
                                  /admin/access-requests is gated on the approver permission. */}
                              <Route path="/request-access" element={<TenantRequired><RequestAccess /></TenantRequired>} />
                              <Route path="/my-requests" element={<TenantRequired><MyAccessRequests /></TenantRequired>} />
                              <Route
                                path="/admin/access-requests"
                                element={
                                  <PermissionRequired permission="access_requests:manage">
                                    <AccessRequestsQueue />
                                  </PermissionRequired>
                                }
                              />
                              {/* Policy version timeline — read-only history with diff modal.
                                  Feature-gated on "abac". */}
                              <Route path="/admin/policies/:id/versions" element={<FeatureRequired feature="abac">{anyPermissionPage([PERMISSIONS.POLICY_READ, PERMISSIONS.POLICY_CONFIGURE], <PolicyVersionTimeline />)}</FeatureRequired>} />
                              {/* Root redirect */}
                              <Route path="/" element={<HomeRedirect />} />

                              {/* Catch-all */}
                              <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                          </Suspense>
                        </BrowserRouter>
                      </AssessmentProvider>
                    </NotificationProvider>
                  </DashboardEditProvider>
                </UseCaseProvider>
              </VendorProvider>
            </TeamProvider>
          </UserProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App
